import { isDebugEnabled } from '../config/debug'

/** PostHog's per-property ceiling is generous but not unlimited, and a long ticket serialises
 * large. Truncated rather than dropped: a clipped dump still shows the shape and the first lines. */
const MAX_CHARS = 20_000

/** The RAW account straight from `pos_getCurrentAccount`, before our normalize.
 *
 * Raw on purpose: normalization is exactly where a wrong assumption hides — it is what turned
 * `itemIdentifier` into `itemId` and made every reward match fail silently on device. A dump of the
 * normalized shape would have shown that as perfectly fine. */
function readRawAccount(): Promise<unknown> {
  return new Promise((resolve) => {
    if (typeof pos_getCurrentAccount !== 'function') {
      resolve({ note: 'no pos host (browser/mock)' })
      return
    }
    // Callback-style with no guarantee it fires: resolve on a timer too, or a silent host would
    // leave this pending and the dump would simply never arrive — the failure mode we are chasing.
    const timer = setTimeout(() => resolve({ note: 'pos_getCurrentAccount never called back' }), 2000)
    try {
      pos_getCurrentAccount((response) => {
        clearTimeout(timer)
        resolve(response?.error ? { error: response.error } : response?.data)
      })
    } catch (e) {
      clearTimeout(timer)
      resolve({ threw: e instanceof Error ? `${e.name}: ${e.message}` : String(e) })
    }
  })
}

/** Ship a raw account dump to PostHog, tagged with WHEN it was taken.
 *
 * Debug-gated. The raw account carries the linked consumer's uid in its external references, and
 * sending that to analytics on every sale is a privacy decision that should not be made silently
 * for production — `?debug=1` (already sticky in localStorage) is the deliberate opt-in.
 *
 * Exists because iterating on POS behaviour from a session replay is nearly blind: console output
 * is not captured, and the interesting state lives in the POS, not the DOM. */
export async function captureAccountDump(
  posthog: { capture: (event: string, properties?: Record<string, unknown>) => void } | undefined,
  stage: string,
  extra?: Record<string, unknown>,
): Promise<void> {
  if (!posthog || !isDebugEnabled()) return
  try {
    const raw = await readRawAccount()
    const json = JSON.stringify(raw)
    posthog.capture('webext_account_dump', {
      stage,
      raw: json.length > MAX_CHARS ? `${json.slice(0, MAX_CHARS)}…[truncated]` : json,
      raw_chars: json.length,
      ...extra,
    })
  } catch {
    // A dump is a diagnostic; it must never break the till flow it is diagnosing.
  }
}
